import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import propTypes from '../../node_modules/prop-types/index.js';
import React, { useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import { DateRangeBoundary } from './constants.js';
import useDateRangeOffset from './useDateRangeOffset.js';
import usePickerInputValue from './usePickerInputValue.js';
import BaseDatePicker from './base/BaseDatePicker.js';

var _excluded = ["value", "onChange", "rangeBoundary", "rangeValue", "inclusiveStart", "inclusiveEnd", "minDate", "maxDate", "slotProps"];
var dateTest = /^\d{4}-\d{2}-\d{2}$/;
var dateFormat = "yyyy-MM-dd";
var dateWarning = "DateLocal only accepts string value `YYYY-MM-DD`";
var offsets = {
  InclusiveStart: 0,
  ExclusiveStart: {
    days: -1
  },
  InclusiveEnd: 0,
  ExclusiveEnd: {
    days: 1
  }
};

var stringToLocalDate = function stringToLocalDate(v) {
  var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  if (!v) return null;

  if (!dateTest.test(v)) {
    console.error(dateWarning);
    return null;
  }

  var dLocal = DateTime.fromFormat(v, dateFormat);
  return dLocal.minus(offset);
};

var localDateToString = function localDateToString(v) {
  var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return v.plus(offset).toFormat(dateFormat);
};

var DATE_LOCAL_FORMAT = dateFormat;

/**
 * Date field for local dates (local time to the user)
 */
var DateLocal = function DateLocal(_ref) {
  var _bounds$min, _bounds$max;

  var value = _ref.value,
      onChange = _ref.onChange,
      rangeBoundary = _ref.rangeBoundary,
      rangeValue = _ref.rangeValue,
      inclusiveStart = _ref.inclusiveStart,
      inclusiveEnd = _ref.inclusiveEnd,
      minDate = _ref.minDate,
      maxDate = _ref.maxDate,
      slotProps = _ref.slotProps,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var valueOffset = useDateRangeOffset(offsets, rangeBoundary, inclusiveStart, inclusiveEnd);
  var startOffset = useDateRangeOffset(offsets, DateRangeBoundary.Start, inclusiveStart, inclusiveEnd);
  var endOffset = useDateRangeOffset(offsets, DateRangeBoundary.End, inclusiveStart, inclusiveEnd);
  var valueDate = useMemo(function () {
    if (!value) return null;
    return stringToLocalDate(value, valueOffset);
  }, [value, valueOffset]);
  var startDate = useMemo(function () {
    switch (rangeBoundary) {
      case DateRangeBoundary.Start:
        // value == range start
        return valueDate;

      case DateRangeBoundary.End:
        // rangeValue == range start
        if (!rangeValue) return null;
        return stringToLocalDate(rangeValue, startOffset);

      default:
        // non-range picker
        return undefined;
    }
  }, [rangeBoundary, valueDate, rangeValue, startOffset]);
  var endDate = useMemo(function () {
    switch (rangeBoundary) {
      case DateRangeBoundary.Start:
        // rangeValue == range end
        if (!rangeValue) return null;
        return stringToLocalDate(rangeValue, endOffset);

      case DateRangeBoundary.End:
        // value == range end
        return valueDate;

      default:
        // non-range picker
        return undefined;
    }
  }, [rangeBoundary, valueDate, rangeValue, endOffset]);

  var _usePickerInputValue = usePickerInputValue(valueDate),
      _usePickerInputValue2 = _slicedToArray(_usePickerInputValue, 2),
      inputValue = _usePickerInputValue2[0],
      setInputValue = _usePickerInputValue2[1];

  var valueOnChange = useCallback(function (v, _ref2) {
    var validationError = _ref2.validationError;
    setInputValue(v);
    if (!onChange) return;

    if (!v || !v.isValid || !!validationError) {
      onChange(null);
      return;
    }

    var newRangeValue = undefined;

    if (rangeBoundary) {
      // ensure range start/end do not overlap
      var comparisonDate = rangeBoundary === DateRangeBoundary.Start ? endDate : startDate;
      var comparisonOffset = rangeBoundary === DateRangeBoundary.Start ? endOffset : startOffset;

      if (comparisonDate && (rangeBoundary === DateRangeBoundary.Start ? v > comparisonDate : v < comparisonDate)) {
        var daysDiff = v.diff(comparisonDate, "days");
        var rAdjusted = comparisonDate.plus(daysDiff);
        newRangeValue = localDateToString(rAdjusted, comparisonOffset);
      }
    }

    onChange(localDateToString(v, valueOffset), newRangeValue);
  }, [onChange, rangeBoundary, startDate, endDate, startOffset, endOffset, valueOffset]);
  var bounds = useMemo(function () {
    return {
      min: typeof minDate === "string" ? stringToLocalDate(minDate, valueOffset) : minDate,
      max: typeof maxDate === "string" ? stringToLocalDate(maxDate, valueOffset) : maxDate
    };
  }, [minDate, maxDate, valueOffset]);
  var textFieldProps = slotProps === null || slotProps === void 0 ? void 0 : slotProps.textField;
  return /*#__PURE__*/React.createElement(BaseDatePicker, _extends({}, otherProps, {
    value: inputValue,
    onChange: valueOnChange,
    rangeBoundary: rangeBoundary,
    rangeStart: startDate,
    rangeEnd: endDate,
    minDate: (_bounds$min = bounds.min) !== null && _bounds$min !== void 0 ? _bounds$min : undefined,
    maxDate: (_bounds$max = bounds.max) !== null && _bounds$max !== void 0 ? _bounds$max : undefined,
    slotProps: _objectSpread2(_objectSpread2({}, slotProps), {}, {
      textField: _objectSpread2(_objectSpread2({}, textFieldProps), {}, {
        onBlur: function onBlur(e) {
          if (inputValue && !valueDate) setInputValue(null);
          if (textFieldProps !== null && textFieldProps !== void 0 && textFieldProps.onBlur) textFieldProps.onBlur(e);
        }
      })
    })
  }));
};

DateLocal.propTypes = {
  value: propTypes.oneOfType([propTypes.string, propTypes.oneOf([null])]),
  onChange: propTypes.func,
  rangeValue: propTypes.oneOfType([propTypes.string, propTypes.oneOf([null])]),
  inclusiveStart: propTypes.bool,
  inclusiveEnd: propTypes.bool,
  minDate: propTypes.oneOfType([propTypes.any, propTypes.string, propTypes.oneOf([null])]),
  maxDate: propTypes.oneOfType([propTypes.any, propTypes.string, propTypes.oneOf([null])])
};
DateLocal.defaultProps = {
  format: "dd/MM/yyyy",
  inclusiveStart: true,
  inclusiveEnd: true
};
var DateLocal$1 = DateLocal;

export { DATE_LOCAL_FORMAT, DateLocal$1 as default };
